/**
 * Utility functions for fetching video titles from URLs
 */

/**
 * Parse a video URL and extract IDs
 * @param {string} url - Video URL
 * @returns {Object|null} Object with youtubeId, youtubePlaylistId, or vimeoId
 */
export function parseVideoUrl(url) {
	if (!url) return null;

	// YouTube playlist patterns
	const playlistOnlyPattern = /youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/;
	const playlistOnlyMatch = url.match(playlistOnlyPattern);
	if (playlistOnlyMatch) {
		return { youtubePlaylistId: playlistOnlyMatch[1] };
	}

	// YouTube video + playlist patterns
	const videoWithPlaylistPattern = /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(?:.*[&?]list=([a-zA-Z0-9_-]+))?/;
	const videoWithPlaylistMatch = url.match(videoWithPlaylistPattern);
	if (videoWithPlaylistMatch) {
		const result = { youtubeId: videoWithPlaylistMatch[1] };
		if (videoWithPlaylistMatch[2]) {
			result.youtubePlaylistId = videoWithPlaylistMatch[2];
		}
		return result;
	}

	// YouTube individual video patterns
	const youtubePatterns = [
		/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
	];

	for (const pattern of youtubePatterns) {
		const match = url.match(pattern);
		if (match) return { youtubeId: match[1] };
	}

	// Vimeo patterns
	const vimeoPattern = /vimeo\.com\/(\d+)/;
	const vimeoMatch = url.match(vimeoPattern);
	if (vimeoMatch) return { vimeoId: vimeoMatch[1] };

	return null;
}

/**
 * Fetch video title from URL using oEmbed APIs
 * @param {string} url - Video URL (YouTube, Vimeo, etc.)
 * @returns {Promise<string>} Video title
 */
export async function fetchVideoTitle(url) {
	if (!url) return 'Untitled Video';

	const videoData = parseVideoUrl(url);
	if (!videoData) return 'Untitled Video';

	try {
		// YouTube video
		if (videoData.youtubeId) {
			const response = await fetch(
				`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoData.youtubeId}&format=json`
			);
			if (response.ok) {
				const data = await response.json();
				return data.title || 'Untitled Video';
			}
		}
		// Vimeo video
		else if (videoData.vimeoId) {
			const response = await fetch(
				`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoData.vimeoId}`
			);
			if (response.ok) {
				const data = await response.json();
				return data.title || 'Untitled Video';
			}
		}
		// YouTube playlist
		else if (videoData.youtubePlaylistId) {
			const response = await fetch(
				`https://www.youtube.com/oembed?url=https://www.youtube.com/playlist?list=${videoData.youtubePlaylistId}&format=json`
			);
			if (response.ok) {
				const data = await response.json();
				return data.title || 'Untitled Playlist';
			}
		}

		return 'Untitled Video';
	} catch (error) {
		console.error('Error fetching video title:', error);
		return 'Untitled Video';
	}
}
